/**
 * Removes the emoji prefix from a string
 * @param text - The string to remove the emoji prefix from
 * @returns The string without the emoji prefix
 * @example
 * removeEmojiPrefix("📱 Profile") // returns "Profile"
 */
const removeEmojiPrefix = (text: string) => {
  return text.replace(/^[^\w\s]+ /, '');
};

/**
 * Constructs a URL path for the settings dashboard with specified tab and section ID
 * @param tab - The settings tab name (e.g., 'general', 'preferences', 'integrations')
 * @param id - The section identifier within the tab
 * @returns A formatted URL path string (e.g., '/dashboard/settings?tab=general#profile')
 */
const constructSettingsPath = (tab: string, id: string) => `/dashboard/settings?tab=${tab}#${id}`;

/**
 * Removes leading emoji and whitespace from a section title and converts to lowercase
 * @param section - The section title that may contain an emoji prefix (e.g., "📱 Profile")
 * @returns The cleaned, lowercase section title (e.g., "profile")
 * @example
 * constructTitle("📱 Profile") // returns "profile"
 * constructTitle("Basic Section") // returns "basic section"
 */
const constructTitle = (section: string): string => {
  return removeEmojiPrefix(section).toLowerCase();
};

/**
 * Converts a hyphenated string to title case (each word capitalized)
 * @param value - The hyphenated string to convert (e.g., "connected-emails")
 * @returns A space-separated string with each word capitalized (e.g., "Connected Emails")
 * @example
 * constructLabel("connected-emails") // returns "Connected Emails"
 * constructLabel("my-custom-label") // returns "My Custom Label"
 */
const constructLabel = (value: string): string =>
  value
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * Converts a section title to a camelCase ID by removing emoji and spaces
 * @param section - The section title (e.g., "📱 Profile" or "Custom Contract Templates")
 * @returns A camelCase ID (e.g., "profile" or "customContractTemplates")
 */
const constructId = (section: string): string => {
  return removeEmojiPrefix(section)
    .split(' ')
    .map((word, index) => (index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1)))
    .join('');
};

export const TABS = {
  GENERAL: {
    value: 'general',
    get label() {
      return constructLabel(this.value);
    },
  },
  PREFERENCES: {
    value: 'preferences',
    get label() {
      return constructLabel(this.value);
    },
  },
  CONNECTED_EMAILS: {
    value: 'connected-emails',
    get label() {
      return constructLabel(this.value);
    },
  },
  INTEGRATIONS: {
    value: 'integrations',
    get label() {
      return constructLabel(this.value);
    },
  },
  SUBSCRIPTION: {
    value: 'subscription',
    get label() {
      return constructLabel(this.value);
    },
  },
} as const;

type BaseSettingType = {
  section: string;
  tab: string;
  id: string;
};

const BASE_SETTINGS: Record<string, BaseSettingType> = {
  PROFILE: {
    section: '📱 Profile',
    tab: TABS.GENERAL.value,
    get id() {
      return constructId(this.section);
    },
  },
  YOUR_TEAM: {
    section: '👥 Your Team',
    tab: TABS.GENERAL.value,
    get id() {
      return constructId(this.section);
    },
  },
  DISCOUNT_CODE_TEMPLATES: {
    section: '🎫 Discount Code Templates',
    tab: TABS.GENERAL.value,
    get id() {
      return constructId(this.section);
    },
  },
  YOUR_COMMISSION_TIERS: {
    section: '💰 Your Commission Tiers',
    tab: TABS.GENERAL.value,
    get id() {
      return constructId(this.section);
    },
  },
  EMAIL_TEMPLATES: {
    section: '⚡ Email Templates',
    tab: TABS.GENERAL.value,
    get id() {
      return constructId(this.section);
    },
  },
  CUSTOM_CONTRACT_TEMPLATES: {
    section: '📄 Custom Contract Templates',
    tab: TABS.GENERAL.value,
    get id() {
      return constructId(this.section);
    },
  },
  AUTOMATIC_ARCHIVING_RULES: {
    section: '🗄️ Automatic Archiving Rules',
    tab: TABS.GENERAL.value,
    get id() {
      return constructId(this.section);
    },
  },
  BLACKLIST_SETTINGS: {
    section: '🚫 Blacklist Settings',
    tab: TABS.GENERAL.value,
    get id() {
      return constructId(this.section);
    },
  },
  PERSONALIZE_SIA: {
    section: '✨ SIA - SARAL Influencer Assistant',
    tab: TABS.PREFERENCES.value,
    get id() {
      return constructId(this.section);
    },
  },
  DYNAMIC_DISCOUNT_CODES: {
    section: '⚡️ Dynamic Discount Codes',
    tab: TABS.PREFERENCES.value,
    get id() {
      return constructId(this.section);
    },
  },
  RELATIONSHIPS: {
    section: '🤝 Relationships',
    tab: TABS.PREFERENCES.value,
    get id() {
      return constructId(this.section);
    },
  },
  STOP_EMAIL_CAMPAIGNS_ON_APPLICATION: {
    section: '✨ Stop Email Campaigns on Application',
    tab: TABS.PREFERENCES.value,
    get id() {
      return constructId(this.section);
    },
  },
  AUTO_ADD_TO_EMAIL_DRIP: {
    section: '📣 Auto Add to Email Drip',
    tab: TABS.PREFERENCES.value,
    get id() {
      return constructId(this.section);
    },
  },
  CUSTOM_TAGS_FOR_ORDERS: {
    section: '🔖 Custom Tags for Orders',
    tab: TABS.PREFERENCES.value,
    get id() {
      return constructId(this.section);
    },
  },
  NEW_CUSTOMER_COMMISSIONS: {
    section: '📊 New Customer Commissions',
    tab: TABS.PREFERENCES.value,
    get id() {
      return constructId(this.section);
    },
  },
  APPLICATION_REMINDER_EMAILS: {
    section: '🔔 Application Reminder Emails',
    tab: TABS.PREFERENCES.value,
    get id() {
      return constructId(this.section);
    },
  },
  HANDLE_DISCOUNT_CODE_CONFLICTS: {
    section: '🔖 Handle Discount Code Conflicts',
    tab: TABS.PREFERENCES.value,
    get id() {
      return constructId(this.section);
    },
  },
  PAID_MEDIA_BENCHMARK: {
    section: '💰 Add your Paid Media CPM Benchmark',
    tab: TABS.PREFERENCES.value,
    get id() {
      return constructId(this.section);
    },
  },
  REFRESH_CREATOR_METRICS: {
    section: '🔄 Refresh Creator Metrics',
    tab: TABS.PREFERENCES.value,
    get id() {
      return constructId(this.section);
    },
  },
  CUSTOMIZE_INTELLIGENT_NAMING: {
    section: '💡 Customize Intelligent Naming',
    tab: TABS.PREFERENCES.value,
    get id() {
      return constructId(this.section);
    },
  },
  NEW_VS_RETURNING_CUSTOMER_INSIGHTS: {
    section: '👥 New vs. Returning Customer Insights',
    tab: TABS.PREFERENCES.value,
    get id() {
      return constructId(this.section);
    },
  },
  COMBINE_DISCOUNT_CODES: {
    section: '🏷️ Combine Discount Codes',
    tab: TABS.PREFERENCES.value,
    get id() {
      return constructId(this.section);
    },
  },
  CUSTOM_RELATIONSHIP_STAGES: {
    section: '🧮 Custom Relationship Stages',
    tab: TABS.PREFERENCES.value,
    get id() {
      return constructId(this.section);
    },
  },
  ADD_UTM_PARAMETERS: {
    section: '📊 Add UTM Parameters',
    tab: TABS.PREFERENCES.value,
    get id() {
      return constructId(this.section);
    },
  },
  SEEDING_COST_TRACKER: {
    section: '💵 Seeding Cost Tracker',
    tab: TABS.PREFERENCES.value,
    get id() {
      return constructId(this.section);
    },
  },
  ADJUST_THE_FAIR_FEE_ESTIMATE: {
    section: '💰 Adjust the Fair Fee Estimate',
    tab: TABS.PREFERENCES.value,
    get id() {
      return constructId(this.section);
    },
  },

  OUT_OF_STOCK_PREFERENCE: {
    section: '🏷️ Show Out of Stock Tags',
    tab: TABS.PREFERENCES.value,
    get id() {
      return constructId(this.section);
    },
  },
  ENABLE_PRODUCT_REQUESTS: {
    section: '🎁 Enable Product Requests',
    tab: TABS.PREFERENCES.value,
    get id() {
      return constructId(this.section);
    },
  },
  REPLY_FOLLOWUP: {
    section: '⏰ Auto-Create Tasks for Follow-ups',
    tab: TABS.PREFERENCES.value,
    get id() {
      return constructId(this.section);
    },
  },
  EMAILS: {
    section: 'Emails',
    tab: TABS.CONNECTED_EMAILS.value,
    get id() {
      return constructId(this.section);
    },
  },
  SHOPIFY_GOAFFPRO: {
    section: 'Shopify + GoAffPro',
    tab: TABS.INTEGRATIONS.value,
    get id() {
      return constructId(this.section);
    },
  },
  KLAVIYO_INTEGRATION: {
    section: 'Klaviyo Integration',
    tab: TABS.INTEGRATIONS.value,
    get id() {
      return constructId(this.section);
    },
  },
  PAYPAL_PAYOUTS: {
    section: 'PayPal Payouts',
    tab: TABS.INTEGRATIONS.value,
    get id() {
      return constructId(this.section);
    },
  },
  WOOCOMMERCE_GOAFFPRO: {
    section: 'WooCommerce + GoAffPro',
    tab: TABS.INTEGRATIONS.value,
    get id() {
      return constructId(this.section);
    },
  },
  SLACK: {
    section: 'Slack',
    tab: TABS.INTEGRATIONS.value,
    get id() {
      return constructId(this.section);
    },
  },
  SENDLANE_INTEGRATION: {
    section: 'Sendlane Integration',
    tab: TABS.INTEGRATIONS.value,
    get id() {
      return constructId(this.section);
    },
  },
  ACTIVE_PLAN: {
    section: 'Active Plan',
    tab: TABS.SUBSCRIPTION.value,
    get id() {
      return constructId(this.section);
    },
  },
  PAYMENT_METHOD: {
    section: 'Payment Method',
    tab: TABS.SUBSCRIPTION.value,
    get id() {
      return constructId(this.section);
    },
  },
  BILLING_INFORMATION: {
    section: 'Billing Information',
    tab: TABS.SUBSCRIPTION.value,
    get id() {
      return constructId(this.section);
    },
  },
  SUBSCRIPTION_HISTORY: {
    section: 'Subscription History',
    tab: TABS.SUBSCRIPTION.value,
    get id() {
      return constructId(this.section);
    },
  },
  CANCEL_ACCOUNT: {
    section: 'Cancel Account',
    tab: TABS.SUBSCRIPTION.value,
    get id() {
      return constructId(this.section);
    },
  },
};

type SettingType = BaseSettingType & {
  title: string;
  path: string;
};

export const SETTINGS_MAP = Object.entries(BASE_SETTINGS).reduce(
  (acc, [key, setting]) => {
    acc[key] = {
      ...setting,
      get title() {
        return constructTitle(this.section);
      },
      get path() {
        return constructSettingsPath(this.tab, this.id);
      },
    };
    return acc;
  },
  {} as Record<string, SettingType>
);

export const DISABLE_VIEW_SETTINGS_BUTTON = ['slack', 'veeper', 'creatorCommerce'];

export const COGS_ACTIONS = {
  KEEP: 'keep',
  UPDATE: 'update',
} as const;
